import React, { useEffect, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Play } from "lucide-react";

const HOLES = 9;
const DURATION = 30;

export function WhackGame({ onScore }) {
  const [active, setActive] = useState(-1);
  const [score, setScore] = useState(0);
  const [time, setTime] = useState(DURATION);
  const [running, setRunning] = useState(false);
  const [over, setOver] = useState(false);
  const [hit, setHit] = useState(-1);
  const reported = useRef(false);
  const moleTimer = useRef(null);

  const popMole = useCallback(() => {
    setActive(Math.floor(Math.random() * HOLES));
    const next = 600 + Math.random() * 700;
    moleTimer.current = setTimeout(popMole, next);
  }, []);

  const start = () => {
    setScore(0); setTime(DURATION); setOver(false); setRunning(true);
    reported.current = false; popMole();
  };

  useEffect(() => {
    if (!running) return;
    if (time <= 0) {
      setRunning(false); setOver(true); setActive(-1);
      clearTimeout(moleTimer.current);
      return;
    }
    const id = setTimeout(() => setTime((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [running, time]);

  useEffect(() => () => clearTimeout(moleTimer.current), []);

  const whack = (i) => {
    if (!running || i !== active) return;
    setScore((s) => s + 1);
    setHit(i);
    setActive(-1);
    setTimeout(() => setHit(-1), 150);
  };

  useEffect(() => {
    if (over && !reported.current) { reported.current = true; onScore?.(score * 20); }
  }, [over, score, onScore]);

  return (
    <div className="flex flex-col items-center" data-testid="whack-game">
      <div className="flex items-center justify-between w-full max-w-sm mb-5 font-mono">
        <span className="text-neon-cyan text-lg" data-testid="whack-score">HITS: {score}</span>
        <span className={`text-lg ${time <= 10 ? "text-neon-pink animate-blink" : "text-neon-yellow"}`}>⏱ {time}s</span>
      </div>

      <div className="relative grid grid-cols-3 gap-3 p-4 bg-zinc-900 rounded-2xl border-2 border-zinc-700" style={{ width: "min(90vw, 360px)" }}>
        {Array.from({ length: HOLES }).map((_, i) => (
          <button key={i} onClick={() => whack(i)} data-testid={`whack-hole-${i}`}
            className="aspect-square rounded-full bg-zinc-950 border-2 border-zinc-800 flex items-center justify-center overflow-hidden relative">
            <div className="text-4xl transition-all duration-150"
              style={{ transform: active === i ? "translateY(0) scale(1)" : "translateY(120%) scale(0.5)",
                       opacity: active === i ? 1 : 0 }}>
              {hit === i ? "💥" : "👾"}
            </div>
          </button>
        ))}

        {!running && (
          <div className="absolute inset-0 bg-black/85 rounded-2xl flex flex-col items-center justify-center gap-3" data-testid="whack-overlay">
            {over && <p className="font-display font-black text-2xl text-neon-lime uppercase" data-testid="whack-result">Time! {score} hits</p>}
            <Button onClick={start} data-testid="whack-start" className="bg-neon-cyan text-black rounded-none font-bold uppercase">
              <Play className="w-4 h-4 mr-1 fill-black" /> {over ? "Play Again" : "Start"}
            </Button>
          </div>
        )}
      </div>
      <p className="text-zinc-500 text-xs font-mono mt-3">Smash the aliens before they hide! 30 seconds.</p>
    </div>
  );
}
